import './CheckRealDowngrade.scss';
import React from 'react';
import Button from '../../../buttons/DeprecatedButton/DeprecatedButton';
import suprisedMonkey from 'assets/monkey-surprised.svg';

type Props = {
  onDowngrade: Function;
  onContinueWorking: Function;
  discount?: string;
  reason: string;
};

const defaultFarewellTexts = {
  title: "Sorry to hear you're leaving…",
  text: `
    We have a team of dedicated Customer Success Representatives
    who are MobileMonkey experts that can help solve these problems.
    Would you like to see if they can help before giving up?
  `,
  buttonCancel: 'Yes, I would love to get this working',
  buttonContinue: 'No, I usually like to just give up',
};

const farewellTexts = {
  "I couldn't figure it out": {
    title: 'Sorry to hear that you had a hard time figuring out our product.',
    text: `
      We have a team of MobileMonkey experts that can help you get your Chatbot up and running.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to get this working',
    buttonContinue: 'No, I usually like to just give up',
  },
  "I'm using another service": {
    title:
      "Sorry to hear that you're leaving because you’re using another service.",
    text: `
      We have MobileMonkey experts on staff that can work something out.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I hope we can work something out',
    buttonContinue: 'No, I usually like to just give up',
  },
  "Chatbots just aren't for me": {
    title: 'Sorry to hear that you think Chatbots aren’t for you.',
    text: `
      We have a team of MobileMonkey experts that can show you all of the great use cases to help your business.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to learn what’s possible',
    buttonContinue: 'No, I usually like to just give up',
  },
  "I couldn't get any contacts": {
    title: 'Sorry to hear that you haven’t been able to get contacts.',
    text: `
      We have a team of MobileMonkey experts that can show you some great strategies
      for growing your Messenger Audience.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I want to grow my audience 😍',
    buttonContinue: 'No, I usually like to just give up 😴',
  },
  "I need features MobileMonkey doesn't support": {
    title: 'Sorry to hear that we don’t have the features you need.',
    text: `
      We have a team of MobileMonkey experts that can let you know if the features you need are in the pipeline,
      or if there’s a workaround we can support today. Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to learn what’s coming up',
    buttonContinue: 'No, I usually like to just give up',
  },
  "I'm having technical problems": {
    title:
      "Sorry to hear that you're leaving because you’re having technical problems.",
    text: `
      We have MobileMonkey experts on staff that can help solve your problems.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to get this working',
    buttonContinue: 'No, I usually like to just give up',
  },
  "I don't use it enough": {
    title:
      'Sorry to hear that you haven’t been able to take full advantage of MobileMonkey.',
    text: `
      We have a team of MobileMonkey experts that can show you all of the great use cases to help your business.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to learn what’s possible 😍',
    buttonContinue: 'No, I usually like to just give up 😴',
  },
  "It's too expensive": {
    title: 'Sorry to hear that MobileMonkey is too expensive for you.',
    text: `
      We have a team of MobileMonkey experts that might be able to help bring your cost down.
      Would you like to see if they can help before giving up?
    `,
    buttonCancel: 'Yes, I would love to make this work',
    buttonContinue: 'No, I usually like to just give up',
  },
};

const CheckRealDowngrade = ({
  onDowngrade,
  onContinueWorking,
  discount,
  reason,
}: Props) => {
  const { title, text, buttonCancel, buttonContinue } =
    farewellTexts[reason] || defaultFarewellTexts;
  return (
    <div className="check-real-downgrade__modal">
      <div className="check-real-downgrade__image-container">
        <img
          className="check-real-downgrade__monkey-img"
          src={suprisedMonkey}
        />
      </div>
      <div className="check-real-downgrade__body">
        <div className="check-real-downgrade__title">{title}</div>
        <div className="check-real-downgrade__sub-title">
          We understand that these problems can be frustrating
        </div>
        <div className="check-real-downgrade__question">{text}</div>
        {discount && (
          <div className="check-real-downgrade__question">
            You have a Discount Coupon worth {discount} off per month, if you
            downgrade you lose this discount forever.
          </div>
        )}
        <div className="check-real-downgrade__buttons">
          <Button
            className="check-real-downgrade__yes-button"
            dirtyBlue
            onClick={onContinueWorking}
          >
            {discount ? "I like savings, I'll stay" : buttonCancel}
          </Button>
          <Button
            className="check-real-downgrade__no-button"
            lightGrayLink
            onClick={onDowngrade}
          >
            {discount ? 'Cancel me anyway, I hate savings' : buttonContinue}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckRealDowngrade;
